import React, { useState, useEffect, useCallback, useMemo } from 'react';

import { AppstoreAddOutlined, SettingOutlined, DeleteOutlined, QuestionCircleFilled } from '@ant-design/icons';
import { Table, Button, Space, Modal, Form, Select, InputNumber, Switch, Drawer, Popconfirm, Input, Tooltip, message } from 'antd';
import { useTranslation } from 'react-i18next';

import ListPageHeader from '@shared/ListPageHeader';
import MerchantSelect from '@components/operator/MerchantSelect';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { merchantShippingTypesService } from '@services/operator/merchantShippingTypesService';
import { shippingTypeService } from '@services/operator/shippingTypeService';
import { shippingRateCardsService } from '@services/operator/shippingRateCardsService';

const MerchantShippingTypesContent = () => {
  const { t } = useTranslation();
  const { merchant } = useOperatorData();
  const merchantId = merchant?.id;

  // 已安裝清單
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // 目錄 Drawer
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [catalogData, setCatalogData] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogKeyword, setCatalogKeyword] = useState('');

  // 編輯費率 Modal
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // 目錄列切換中的 shipping_type_id 集合（用於顯示 loading）
  const [togglingIds, setTogglingIds] = useState(() => new Set());

  // Rate cards
  const [rateCards, setRateCards] = useState([]);

  // ── Data Fetching ──

  const fetchInstalled = useCallback(async () => {
    if (!merchantId) return;
    setLoading(true);
    const result = await merchantShippingTypesService.index(merchantId);
    if (result.success) {
      setData(result.data || []);
    } else {
      message.error(result.error);
    }
    setLoading(false);
  }, [merchantId]);

  const fetchRateCards = useCallback(async () => {
    try {
      const res = await shippingRateCardsService.fetchList();
      setRateCards(res.data || []);
    } catch (err) {
      console.error('Failed to fetch rate cards:', err);
    }
  }, []);

  useEffect(() => {
    fetchInstalled();
    fetchRateCards();
  }, [fetchInstalled, fetchRateCards]);

  const fetchCatalog = useCallback(async () => {
    setCatalogLoading(true);
    const result = await shippingTypeService.listAll();
    if (result.success) {
      // 過濾掉 platform 來源
      const filtered = (result.data || []).filter(st => st.source !== 'platform');
      setCatalogData(filtered);
    } else {
      message.error(result.error);
    }
    setCatalogLoading(false);
  }, []);

  // 已安裝的 shipping_type_id Set，用於目錄比對
  const installedTypeIds = useMemo(
    () => new Set(data.map(d => d.shipping_type_id)),
    [data]
  );

  // shipping_type_id → merchant_shipping_type 的對應，供目錄切換時停用使用
  const installedByTypeId = useMemo(() => {
    const map = new Map();
    data.forEach(d => map.set(d.shipping_type_id, d));
    return map;
  }, [data]);

  // ── Drawer Handlers ──

  const handleOpenCatalog = () => {
    setDrawerVisible(true);
    fetchCatalog();
  };

  const filteredCatalogData = useMemo(() => {
    if (!catalogKeyword) return catalogData;
    const kw = catalogKeyword.toLowerCase();
    return catalogData.filter(item =>
      item.code.toLowerCase().includes(kw) ||
      item.name.toLowerCase().includes(kw)
    );
  }, [catalogData, catalogKeyword]);

  // ── Toggle Enable / Disable ──

  const markToggling = (typeId, on) => {
    setTogglingIds(prev => {
      const next = new Set(prev);
      if (on) next.add(typeId); else next.delete(typeId);
      return next;
    });
  };

  const handleToggleEnable = async (shippingType, checked) => {
    markToggling(shippingType.id, true);
    try {
      if (checked) {
        const result = await merchantShippingTypesService.create({
          merchant_id: merchantId,
          shipping_type_id: shippingType.id,
        });
        if (result.success) {
          message.success(t('operator.pages.master_data.merchant_shipping_types.enable_success'));
          fetchInstalled();
        } else {
          message.error(result.error);
        }
      } else {
        const installed = installedByTypeId.get(shippingType.id);
        if (!installed) return;
        const result = await merchantShippingTypesService.destroy(installed.id);
        if (result.success) {
          message.success(t('operator.pages.master_data.merchant_shipping_types.disable_success'));
          fetchInstalled();
        } else {
          message.error(result.error);
        }
      }
    } finally {
      markToggling(shippingType.id, false);
    }
  };

  // ── Rate Modal Handlers ──

  const handleEditRate = (record) => {
    setEditingRecord(record);
    form.resetFields();
    form.setFieldsValue({
      shipping_rate_card_id: record.shipping_rate_card_id,
      price: record.price,
      return_shipping_fee: record.return_shipping_fee,
    });
    setModalVisible(true);
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const result = await merchantShippingTypesService.update(editingRecord.id, values);
      if (result.success) {
        message.success(t('operator.pages.master_data.merchant_shipping_types.update_success'));
        handleModalCancel();
        fetchInstalled();
      } else {
        message.error(result.error);
      }
    } catch (error) {
      // validation failed
    } finally {
      setSaving(false);
    }
  };

  // ── Uninstall (from main list) ──

  const handleDisable = async (record) => {
    const result = await merchantShippingTypesService.destroy(record.id);
    if (result.success) {
      message.success(t('operator.pages.master_data.merchant_shipping_types.disable_success'));
      fetchInstalled();
    } else {
      message.error(result.error);
    }
  };

  // ── Table Columns ──

  const columns = useMemo(() => [
    {
      title: t('models.shipping_type.name'),
      key: 'shipping_type_name',
      render: (_, record) => (
        <span>
          {t(`shipping_types.${record.shipping_type_code}`, { defaultValue: record.shipping_type_name })}
          <div style={{ fontSize: '12px', color: '#888' }}>{record.shipping_type_code}</div>
        </span>
      ),
    },
    {
      title: t('operator.menu.shipping_settings.shipping_rate_cards'),
      dataIndex: 'shipping_rate_card_name',
      key: 'shipping_rate_card_name',
    },
    {
      title: t('common.fallback_price'),
      dataIndex: 'price',
      key: 'price',
      width: 120,
    },
    {
      title: t('operator.pages.master_data.merchant_shipping_types.return_shipping_fee'),
      dataIndex: 'return_shipping_fee',
      key: 'return_shipping_fee',
      width: 120,
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<SettingOutlined />}
            onClick={() => handleEditRate(record)}
          >
            {t('operator.pages.master_data.merchant_shipping_types.configure_rate')}
          </Button>
          <Popconfirm
            title={t('operator.pages.master_data.merchant_shipping_types.confirm_disable')}
            onConfirm={() => handleDisable(record)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ], [t]);

  const catalogColumns = useMemo(() => [
    {
      title: t('models.shipping_type.code'),
      dataIndex: 'code',
      key: 'code',
      width: 180,
    },
    {
      title: t('models.shipping_type.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('common.enabled'),
      key: 'enabled',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Switch
          data-testid="merchant-shipping-enable-switch"
          checked={installedTypeIds.has(record.id)}
          loading={togglingIds.has(record.id)}
          onChange={(checked) => handleToggleEnable(record, checked)}
        />
      ),
    },
  ], [t, installedTypeIds, togglingIds]);

  // ── Render ──

  return (
    <div className="page-container">
      <ListPageHeader
        items={['merchant_settings', 'merchant_settings.merchant_shipping_types']}
        guideButton={
          <Tooltip
            title={t('operator.pages.master_data.merchant_shipping_types.page_help')}
            overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 480 }}
          >
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
              style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
            />
          </Tooltip>
        }
        actions={
          <Button data-testid="merchant-shipping-enable-btn" icon={<AppstoreAddOutlined />} onClick={handleOpenCatalog}>
            {t('operator.pages.master_data.merchant_shipping_types.enable_shipping')}
          </Button>
        }
      />
      <Table
        data-testid="merchant-shipping-table"
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={false}
      />

      {/* 可安裝目錄 Drawer */}
      <Drawer
        data-testid="merchant-shipping-catalog-drawer"
        title={t('operator.pages.master_data.merchant_shipping_types.catalog_title')}
        placement="right"
        width={700}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        <div style={{ marginBottom: 16 }}>
          <Input.Search
            placeholder={t('common.placeholder.search_keyword')}
            onSearch={(value) => setCatalogKeyword(value)}
            onChange={(e) => { if (!e.target.value) setCatalogKeyword(''); }}
            allowClear
            style={{ width: 300 }}
          />
        </div>
        <Table
          columns={catalogColumns}
          dataSource={filteredCatalogData}
          rowKey="id"
          loading={catalogLoading}
          pagination={{ showSizeChanger: true, defaultPageSize: 20 }}
          size="small"
        />
      </Drawer>

      {/* 編輯費率 Modal */}
      <Modal
        data-testid="merchant-shipping-modal"
        title={t('operator.pages.master_data.merchant_shipping_types.modal_title_edit')}
        open={modalVisible}
        onOk={handleModalSubmit}
        onCancel={handleModalCancel}
        confirmLoading={saving}
        okButtonProps={{ 'data-testid': 'merchant-shipping-modal-ok-btn' }}
      >
        {editingRecord && (
          <div style={{ marginBottom: 16 }}>
            <strong>{t('models.shipping_type.name')}:</strong>{' '}
            {t(`shipping_types.${editingRecord.shipping_type_code}`, { defaultValue: editingRecord.shipping_type_name })}
          </div>
        )}
        <Form form={form} layout="vertical">
          <Form.Item
            name="shipping_rate_card_id"
            label={t('operator.menu.shipping_settings.shipping_rate_cards')}
            rules={[{ required: false }]}
          >
            <Select
              data-testid="merchant-shipping-rate-card-select"
              placeholder={t('common.placeholder.select')}
              options={rateCards.map(c => ({ label: c.name, value: c.id }))}
              allowClear
              getPopupContainer={node => node.parentNode}
            />
          </Form.Item>
          <Form.Item
            name="price"
            label={t('common.fallback_price')}
          >
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="return_shipping_fee"
            label={t('operator.pages.master_data.merchant_shipping_types.return_shipping_fee')}
          >
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const MerchantShippingTypesList = () => {
  return (
    <MerchantSelect>
      <MerchantShippingTypesContent />
    </MerchantSelect>
  );
};

export default MerchantShippingTypesList;
